/**
 * Confirm Dialog Composable
 * Promise-based confirmation dialog.
 *
 * Usage:
 * ```ts
 * const confirm = useConfirm()
 * const ok = await confirm({
 *   title: 'Delete Project',
 *   message: 'Are you sure? This cannot be undone.',
 *   confirmText: 'Delete',
 *   variant: 'danger',
 * })
 * if (ok) { ... }
 * ```
 */

import { reactive, readonly } from 'vue'

export interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'default'
}

interface ConfirmState {
  visible: boolean
  options: ConfirmOptions
  resolve: ((value: boolean) => void) | null
}

// Shared reactive state (singleton — only one confirm dialog at a time)
const state = reactive<ConfirmState>({
  visible: false,
  options: {
    title: 'Confirm',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'danger',
  },
  resolve: null,
})

function openConfirm(options: ConfirmOptions): Promise<boolean> {
  // If already showing, reject previous
  if (state.resolve) {
    state.resolve(false)
  }

  return new Promise<boolean>((resolve) => {
    state.options = {
      title: options.title ?? 'Confirm',
      message: options.message,
      confirmText: options.confirmText ?? 'Confirm',
      cancelText: options.cancelText ?? 'Cancel',
      variant: options.variant ?? 'danger',
    }
    state.resolve = resolve
    state.visible = true
  })
}

function handleConfirm() {
  if (state.resolve) {
    state.resolve(true)
    state.resolve = null
  }
  state.visible = false
}

function handleCancel() {
  if (state.resolve) {
    state.resolve(false)
    state.resolve = null
  }
  state.visible = false
}

/**
 * Composable for confirm dialogs.
 * Returns a callable that opens the dialog and returns a Promise<boolean>.
 */
export function useConfirm() {
  // Return the opener function directly — enables `const confirm = useConfirm(); await confirm({...})`
  const confirmFn = (options: ConfirmOptions) => openConfirm(options)

  // Attach state for the ConfirmDialog.vue component to read
  confirmFn.state = readonly(state)
  confirmFn.handleConfirm = handleConfirm
  confirmFn.handleCancel = handleCancel

  return confirmFn
}

// Export state accessors for the ConfirmDialog component
export const confirmState = readonly(state)
export { handleConfirm, handleCancel }
